import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useMediaQuery
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../hooks/useAuth';
import { useLanguage } from '../../i18n/LanguageContext';
import { NotificationBadge } from './NotificationBadge';

type ViewType = 'calendar' | 'summary' | 'exercises' | 'bodyweight' | 'routines' | 'programs' | 'friends' | 'profile' | 'chat' | 'evaluation' | 'wrapped';

interface NavigationProps {
  view: ViewType;
  setView: (view: ViewType) => void;
  onReleaseNotesClick: () => void;
  fetchWithAuth: (url: string, options?: any) => Promise<Response>;
  onNotificationRefresh?: (refreshFunction: () => Promise<void>) => void;
  authToken: string | null;
}

export const Navigation: React.FC<NavigationProps> = ({ view, setView, onReleaseNotesClick, fetchWithAuth, onNotificationRefresh, authToken }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { handleLogout } = useAuth();
  const { t } = useLanguage();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogoutClick = () => {
    setLogoutDialogOpen(true);
  };

  const handleLogoutConfirm = () => {
    handleLogout();
    setLogoutDialogOpen(false);
  };

  const handleLogoutCancel = () => {
    setLogoutDialogOpen(false);
  };

  const drawer = (
    <List>
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('calendar'); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.calendar')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('summary'); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.summary')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('wrapped'); setMobileOpen(false); }}>
          <ListItemText primary={t('wrapped.navTitle')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('exercises'); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.exercises')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('bodyweight'); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.bodyweight')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('routines'); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.routines')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('programs'); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.programs')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('friends'); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.friends')} />
        </ListItemButton>
      </ListItem>
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('chat'); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.chat')} />
        </ListItemButton>
      </ListItem>
      {process.env.NODE_ENV === 'development' && (
        <ListItem disablePadding>
          <ListItemButton onClick={() => { setView('evaluation'); setMobileOpen(false); }}>
            <ListItemText primary={t('nav.evaluation')} />
          </ListItemButton>
        </ListItem>
      )}
      <ListItem disablePadding>
        <ListItemButton onClick={() => { setView('profile'); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.profile')} />
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton onClick={() => { onReleaseNotesClick(); setMobileOpen(false); }}>
          <ListItemText primary={t('nav.releaseNotes')} />
        </ListItemButton>
      </ListItem>

      <ListItem disablePadding>
        <ListItemButton onClick={handleLogoutClick}>
          <ListItemText primary={t('nav.logout')} />
        </ListItemButton>
      </ListItem>
    </List>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            {t('nav.appTitle')}
          </Typography>

          {/* Show notification badge on both mobile and desktop */}
          <NotificationBadge
            fetchWithAuth={fetchWithAuth}
            setView={setView}
            onRefresh={onNotificationRefresh}
            authToken={authToken}
          />

          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
              <Button
                color="inherit"
                onClick={() => setView('calendar')}
                sx={{ fontWeight: view === 'calendar' ? 'bold' : 'normal' }}
              >
                {t('nav.calendar')}
              </Button>
              <Button
                color="inherit"
                onClick={() => setView('summary')}
                sx={{ fontWeight: view === 'summary' ? 'bold' : 'normal' }}
              >
                {t('nav.summary')}
              </Button>
              <Button
                onClick={() => setView('wrapped')}
                sx={{
                  fontWeight: view === 'wrapped' ? 'bold' : 'normal',
                  background: 'linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  border: '1px solid #FF8E53',
                  borderRadius: '20px',
                  px: 2
                }}
              >
                {t('wrapped.navTitle')}
              </Button>
              <Button
                color="inherit"
                onClick={() => setView('exercises')}
                sx={{ fontWeight: view === 'exercises' ? 'bold' : 'normal' }}
              >
                {t('nav.exercises')}
              </Button>
              <Button
                color="inherit"
                onClick={() => setView('bodyweight')}
                sx={{ fontWeight: view === 'bodyweight' ? 'bold' : 'normal' }}
              >
                {t('nav.bodyweight')}
              </Button>
              <Button
                color="inherit"
                onClick={() => setView('routines')}
                sx={{ fontWeight: view === 'routines' ? 'bold' : 'normal' }}
              >
                {t('nav.routines')}
              </Button>
              <Button
                color="inherit"
                onClick={() => setView('programs')}
                sx={{ fontWeight: view === 'programs' ? 'bold' : 'normal' }}
              >
                {t('nav.programs')}
              </Button>
              <Button
                color="inherit"
                onClick={() => setView('friends')}
                sx={{ fontWeight: view === 'friends' ? 'bold' : 'normal' }}
              >
                {t('nav.friends')}
              </Button>
              <Button
                color="inherit"
                onClick={() => setView('chat')}
                sx={{ fontWeight: view === 'chat' ? 'bold' : 'normal' }}
              >
                {t('nav.chat')}
              </Button>
              {process.env.NODE_ENV === 'development' && (
                <Button
                  color="inherit"
                  onClick={() => setView('evaluation')}
                  sx={{ fontWeight: view === 'evaluation' ? 'bold' : 'normal' }}
                >
                  {t('nav.evaluation')}
                </Button>
              )}
              <Button
                color="inherit"
                onClick={() => setView('profile')}
                sx={{ fontWeight: view === 'profile' ? 'bold' : 'normal' }}
              >
                {t('nav.profile')}
              </Button>
              <Button
                color="inherit"
                onClick={onReleaseNotesClick}
              >
                {t('nav.releaseNotes')}
              </Button>

              <Button color="inherit" onClick={handleLogoutClick}>
                {t('nav.logout')}
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 240 },
        }}
      >
        {drawer}
      </Drawer>

      <Dialog
        open={logoutDialogOpen}
        onClose={handleLogoutCancel}
      >
        <DialogTitle>{t('logout.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('logout.message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLogoutCancel}>
            {t('logout.cancel')}
          </Button>
          <Button onClick={handleLogoutConfirm} color="primary" autoFocus>
            {t('logout.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 